<script setup lang="ts">
defineOptions({ name: 'TqSystemLoadingOverlay' })

import {
  subscribeToInterfaceLoadActivity
} from '~/shared-kernel/application/loading/interface-load-activity'

const { isLoading, progress } = useLoadingIndicator({
  duration: 2200,
  throttle: 90,
  hideDelay: 140,
  resetDelay: 0
})
const router = useRouter()

const LONG_INTERFACE_LOAD_DELAY_MS = 450
const INTERFACE_LOAD_IDLE_GRACE_MS = 100
const MINIMUM_VISIBLE_MS = 360
const activeInterfaceLoads = ref(0)
const longInterfaceLoadVisible = ref(false)
const routeInterfaceNavigation = ref(true)
let revealTimer: ReturnType<typeof setTimeout> | undefined
let idleTimer: ReturnType<typeof setTimeout> | undefined
let hideTimer: ReturnType<typeof setTimeout> | undefined
let thresholdReachedWhileIdle = false
let visibleSince = 0
let unsubscribe: (() => void) | undefined
let removeNavigationIntentGuard: (() => void) | undefined

const progressScale = computed(() => Math.max(0, Math.min(1, progress.value / 100)))
const routeLoadVisible = computed(() => isLoading.value && routeInterfaceNavigation.value)
const interfaceLoadOnly = computed(() => longInterfaceLoadVisible.value && !routeLoadVisible.value)
const overlayVisible = computed(() => routeLoadVisible.value || longInterfaceLoadVisible.value)

function clearRevealTimer() {
  if (revealTimer !== undefined) clearTimeout(revealTimer)
  revealTimer = undefined
}

function clearHideTimer() {
  if (hideTimer !== undefined) clearTimeout(hideTimer)
  hideTimer = undefined
}

function clearIdleTimer() {
  if (idleTimer !== undefined) clearTimeout(idleTimer)
  idleTimer = undefined
}

function hideLongInterfaceLoad() {
  hideTimer = undefined
  longInterfaceLoadVisible.value = false
  visibleSince = 0
}

function revealLongInterfaceLoad() {
  thresholdReachedWhileIdle = false
  visibleSince = Date.now()
  longInterfaceLoadVisible.value = true
}

function settleInterfaceLoadOverlay() {
  idleTimer = undefined
  if (activeInterfaceLoads.value > 0) return

  thresholdReachedWhileIdle = false
  clearRevealTimer()
  if (!longInterfaceLoadVisible.value) return

  const remaining = MINIMUM_VISIBLE_MS - (Date.now() - visibleSince)
  if (remaining <= 0) {
    hideLongInterfaceLoad()
    return
  }

  clearHideTimer()
  hideTimer = setTimeout(hideLongInterfaceLoad, remaining)
}

function synchronizeInterfaceLoadOverlay(count: number) {
  activeInterfaceLoads.value = count

  if (count > 0) {
    const resumedWithinGrace = idleTimer !== undefined
    clearIdleTimer()
    clearHideTimer()
    if (longInterfaceLoadVisible.value) return

    if (resumedWithinGrace && thresholdReachedWhileIdle) {
      revealLongInterfaceLoad()
      return
    }

    if (revealTimer !== undefined) return

    revealTimer = setTimeout(() => {
      revealTimer = undefined
      if (activeInterfaceLoads.value === 0) {
        thresholdReachedWhileIdle = idleTimer !== undefined
        return
      }
      revealLongInterfaceLoad()
    }, LONG_INTERFACE_LOAD_DELAY_MS)
    return
  }

  if (
    revealTimer === undefined
    && !longInterfaceLoadVisible.value
    && !thresholdReachedWhileIdle
  ) return

  clearIdleTimer()
  idleTimer = setTimeout(settleInterfaceLoadOverlay, INTERFACE_LOAD_IDLE_GRACE_MS)
}

onMounted(() => {
  removeNavigationIntentGuard = router.beforeEach((to, from) => {
    routeInterfaceNavigation.value = to.path !== from.path && !document.documentElement.dataset.ticketDirection
  })
  unsubscribe = subscribeToInterfaceLoadActivity(synchronizeInterfaceLoadOverlay)
})

onBeforeUnmount(() => {
  unsubscribe?.()
  removeNavigationIntentGuard?.()
  clearRevealTimer()
  clearIdleTimer()
  clearHideTimer()
})
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="tq-system-loading">
        <div
          v-if="overlayVisible"
          class="tq-system-loading"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-busy="true"
          data-testid="system-loading-overlay"
        >
          <div class="tq-system-loading__panel">
            <div class="tq-system-loading__orb">
              <TqSolvingOrb />
            </div>

            <div class="tq-system-loading__copy">
              <strong>
                {{ $t(interfaceLoadOnly ? 'interfaceLoading.title' : 'pageLoading.title') }}
              </strong>
              <span>
                {{ $t(interfaceLoadOnly ? 'interfaceLoading.description' : 'pageLoading.description') }}
              </span>
            </div>

            <span class="tq-system-loading__track" aria-hidden="true">
              <span
                class="tq-system-loading__progress"
                :class="{ 'tq-system-loading__progress--indeterminate': interfaceLoadOnly }"
                :style="interfaceLoadOnly ? undefined : { transform: `scaleX(${progressScale})` }"
              />
            </span>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
.tq-system-loading {
  position: fixed;
  z-index: 2147483000;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1.25rem;
  background:
    radial-gradient(circle at 50% 43%, color-mix(in oklch, var(--tq-color-teal) 6%, transparent), transparent 17rem),
    radial-gradient(circle at 57% 52%, color-mix(in oklch, var(--tq-color-orange) 4%, transparent), transparent 22rem),
    color-mix(in oklch, var(--tq-bg) 88%, transparent);
  cursor: wait;
}

.tq-system-loading__panel {
  display: grid;
  width: min(22rem, 100%);
  justify-items: center;
  padding: 1rem;
}

.tq-system-loading__orb {
  position: relative;
  display: grid;
  min-height: 7.75rem;
  place-items: center;
}

.tq-system-loading__copy {
  display: grid;
  gap: 0.3rem;
  justify-items: center;
  margin-top: 0.45rem;
  text-align: center;
}

.tq-system-loading__copy strong {
  color: var(--tq-text);
  font-size: 1rem;
  font-weight: var(--tq-font-weight-bold);
  letter-spacing: -0.015em;
}

.tq-system-loading__copy span {
  color: var(--tq-text-muted);
  font-size: 0.78rem;
  line-height: 1.5;
}

.tq-system-loading__track {
  width: min(10rem, 72%);
  height: 0.18rem;
  overflow: hidden;
  margin-top: 1.1rem;
  border-radius: 999px;
  background: color-mix(in oklch, var(--tq-color-navy) 10%, var(--tq-bg-strong));
}

.tq-system-loading__progress {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    color-mix(in oklch, var(--tq-color-navy) 72%, var(--tq-text-muted)),
    color-mix(in oklch, var(--tq-color-teal) 64%, var(--tq-text-muted)) 48%,
    color-mix(in oklch, var(--tq-color-orange) 58%, var(--tq-text-muted)) 78%,
    color-mix(in oklch, var(--tq-color-yellow) 46%, var(--tq-text-muted))
  );
  transform-origin: left center;
  transition: transform 180ms var(--tq-ease-out);
}

.tq-system-loading__progress--indeterminate {
  width: 42%;
  animation: tq-interface-loading-progress 1.15s ease-in-out infinite;
  transform: translateX(-115%);
}

@keyframes tq-interface-loading-progress {
  50% {
    transform: translateX(238%);
  }
  100% {
    transform: translateX(-115%);
  }
}

.tq-system-loading-enter-active,
.tq-system-loading-leave-active {
  transition: opacity 180ms var(--tq-ease-out);
}

.tq-system-loading-enter-active .tq-system-loading__panel,
.tq-system-loading-leave-active .tq-system-loading__panel {
  transition:
    opacity 180ms var(--tq-ease-out),
    transform 220ms var(--tq-ease-out);
}

.tq-system-loading-enter-from,
.tq-system-loading-leave-to,
.tq-system-loading-enter-from .tq-system-loading__panel,
.tq-system-loading-leave-to .tq-system-loading__panel {
  opacity: 0;
}

.tq-system-loading-enter-from .tq-system-loading__panel {
  transform: translateY(0.45rem) scale(0.985);
}

.tq-system-loading-leave-to .tq-system-loading__panel {
  transform: translateY(-0.25rem) scale(0.99);
}

@media (max-width: 480px) {
  .tq-system-loading__panel {
    width: min(19rem, 100%);
    padding-inline: 1.25rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tq-system-loading__progress {
    transition: none;
  }

  .tq-system-loading__progress--indeterminate {
    animation: none;
    transform: translateX(70%);
  }
}
</style>
